import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ProductCard from "@/components/ProductCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sparkles, Search } from "lucide-react";
import { VoiceSearchButton } from "@/components/VoiceSearchButton";
import { FirstDemandPrompt } from "@/components/FirstDemandPrompt";
import { TID } from "@/constants/tid";
import { CATEGORY_SLUGS, categoryLabel, CATEGORIES } from "@/constants/categories";
import { useI18n } from "@/i18n/I18nContext";

const CATS = ["all", ...CATEGORY_SLUGS];

export default function Discover() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [reco, setReco] = useState(null); // { reason, products }
  const [q, setQ] = useState("");
  const [cat, setCatState] = useState(() => {
    const c = searchParams.get("category");
    return c && CATS.includes(c) ? c : "all";
  });
  const setCat = (c) => {
    setCatState(c);
    setSearchParams(c === "all" ? {} : { category: c }, { replace: true });
  };
  const sellerFilter = searchParams.get("seller") || "";
  const [loading, setLoading] = useState(true);
  const [loadingReco, setLoadingReco] = useState(false);
  const [aiMatched, setAiMatched] = useState(false);
  const [forYou, setForYou] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        if (q && q.trim().length >= 3) {
          try {
            const { data } = await api.get("/match/search", {
              params: { q: q.trim(), ...(cat !== "all" ? { category: cat } : {}) },
            });
            setProducts(data.results);
            setAiMatched(true);
            setForYou(false);
            return;
          } catch {
            // fall back to keyword search below
          }
        }
        if (!q && !sellerFilter && user && user !== false && user.role === "buyer") {
          try {
            const { data } = await api.get("/match/feed", {
              params: cat !== "all" ? { category: cat } : {},
            });
            setProducts(data.results);
            setAiMatched(false);
            setForYou(data.personalized);
            return;
          } catch {
            // fall back to default listing below
          }
        }
        const params = {};
        if (q) params.search = q;
        if (cat !== "all") params.category = cat;
        if (sellerFilter) params.seller = sellerFilter;
        const { data } = await api.get("/products", { params });
        setProducts(data);
        setAiMatched(false);
        setForYou(false);
      } finally {
        setLoading(false);
      }
    };
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q, cat, user, sellerFilter]);

  useEffect(() => {
    const loadReco = async () => {
      if (!user || user === false || user.role !== "buyer") return;
      setLoadingReco(true);
      try {
        const { data } = await api.get("/ai/recommendations");
        setReco(data);
      } catch {
        setReco(null);
      } finally {
        setLoadingReco(false);
      }
    };
    loadReco();
  }, [user]);

  const grid = useMemo(() => products, [products]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <FirstDemandPrompt />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#007AFF]">Live discovery</div>
          <h1 className="mt-2 font-black tracking-tight text-4xl">{t("discover_title", "Fresh drops, always live")}</h1>
          <p className="text-neutral-400 mt-2 max-w-xl">
            New products land here every day and stay live until they sell out. The freshest drops surface first — move fast on the hottest ones.
          </p>
        </div>
        <div className="w-full md:w-96 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
          <Input
            data-testid={TID.discoverSearch}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("discover_search_ph", "Search titles, tags… or tap the mic")}
            className="pl-9 pr-10 bg-[#121212] border-[#27272A]"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <VoiceSearchButton onResult={(query) => setQ(query)} />
          </div>
          <div className="mt-2 flex items-center justify-between gap-2">
            {aiMatched && q ? (
              <span data-testid="ai-match-badge" className="text-[10px] uppercase tracking-wider text-[#34C759] flex items-center gap-1">
                <Sparkles className="h-3 w-3" /> AI semantic match
              </span>
            ) : forYou ? (
              <span data-testid="for-you-badge" className="text-[10px] uppercase tracking-wider text-[#007AFF] flex items-center gap-1">
                <Sparkles className="h-3 w-3" /> Ranked for you
              </span>
            ) : <span />}
            <a href="/wanted" data-testid="wanted-link" className="text-xs text-[#FF3B30] hover:underline font-semibold">
              Can't find it? Let AI hunt for you →
            </a>
          </div>
        </div>
      </div>

      {sellerFilter && (
        <div className="mt-5 flex items-center gap-3" data-testid="seller-filter-chip">
          <span className="text-xs uppercase tracking-widest text-[#FF3B30] font-bold">{t("founder_shop", "Founder shop")}</span>
          <button
            data-testid="clear-seller-filter"
            onClick={() => setSearchParams({}, { replace: true })}
            className="text-xs text-neutral-400 hover:text-white underline"
          >
            {t("show_all", "Show all products")}
          </button>
        </div>
      )}

      {/* AI reco strip */}
      {user && user !== false && user.role === "buyer" && (
        <section data-testid={TID.recoRow} className="mt-10 rounded-xl border border-[#27272A] bg-gradient-to-b from-[#121212] to-[#0A0A0A] p-6">
          <div className="flex items-center gap-2 text-[#007AFF]">
            <Sparkles className="h-4 w-4" />
            <div className="text-xs uppercase tracking-widest">AI curated for you</div>
          </div>
          <div className="mt-2 flex items-baseline justify-between gap-4 flex-wrap">
            <h2 className="font-black text-2xl tracking-tight">
              {loadingReco ? "Matching you to live drops…" : "Top picks right now"}
            </h2>
            <p data-testid={TID.recoReason} className="text-neutral-400 text-sm max-w-lg">
              {reco?.reason || "Add interests to your profile for personalized picks."}
            </p>
          </div>
          {reco?.products?.length > 0 && (
            <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {reco.products.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Category chips */}
      <div className="mt-10 flex flex-wrap gap-2">
        {CATS.map((c) => (
          <button
            key={c}
            data-testid={c === "all" ? TID.discoverCategoryAll : `discover-category-${c}`}
            onClick={() => setCat(c)}
            title={c === "all" ? "Everything live right now" : CATEGORIES.find((x) => x.slug === c)?.subtitle}
            className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-widest border ${
              cat === c
                ? "bg-white text-black border-white"
                : "bg-[#121212] text-neutral-400 border-[#27272A] hover:text-white"
            }`}
            style={{ transitionProperty: "background-color, color, border-color", transitionDuration: "200ms" }}
          >
            {c === "all" ? "all" : categoryLabel(c)}
          </button>
        ))}
      </div>

      {/* Grid */}
      <div data-testid={TID.discoverGrid} className="mt-8">
        {loading ? (
          <div className="text-neutral-500">Loading…</div>
        ) : grid.length === 0 ? (
          <div className="border border-dashed border-[#27272A] rounded-xl p-16 text-center">
            <div className="font-bold text-lg">No live products match your filters.</div>
            <div className="text-neutral-400 text-sm mt-2">Try clearing the search or picking another category.</div>
            <Button onClick={() => { setQ(""); setCat("all"); }} className="mt-6 bg-white text-black hover:bg-neutral-200 rounded-full">
              Reset filters
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {grid.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
